import { FlexProps, Flex } from '@chakra-ui/react'
import {
  ReactElement,
  Children,
  cloneElement,
  isValidElement,
  JSXElementConstructor,
  ReactPortal,
} from 'react'

import { space } from '../../theme/tokens'
import { componentSpacing } from '../../theme/components'

/**
 * A map between flex direction and the property to apply spacing to
 */
const spacingDirectionProp = {
  column: 'mb',
  'column-reverse': 'mt',
  row: 'mr',
  'row-reverse': 'ml',
} as const

export type ContentStackProps = {
  children: Array<ReactElement | null> | ReactElement | null
  defaultSpacing?: keyof typeof space | 0
  direction?: keyof typeof spacingDirectionProp
} & Omit<FlexProps, 'children' | 'direction'>

/**
 * Resolve a type name
 */
const elementTypeName = (
  child: ReactElement<any, string | JSXElementConstructor<any>> | ReactPortal
) => {
  const elTypeOrString = child.type

  if (typeof elTypeOrString === 'string') {
    return elTypeOrString
  }

  return elTypeOrString.name ?? (elTypeOrString as any).displayName
}

/**
 * Like a Chakra stack but works with more complex spacing rules to
 * handle rythmic rules between typography and other content elements
 *
 * Note per-component spacing is sensitive to react type.name and so for
 * every component you want to have default spacing you must add a
 * definition to the componentSpacing token
 *
 * @see componentSpacing token definition
 */
export const ContentStack = ({
  children,
  direction = 'column',
  defaultSpacing = componentSpacing.default.default,
  ...otherProps
}: ContentStackProps) => {
  const spacingProp = spacingDirectionProp[direction]
  const childArray = Children.toArray(children)
  const numChildren = childArray.length
  const mappedChildren = Children.map(children, (child, index) => {
    if (!child) {
      return null
    }

    const currentName = elementTypeName(child)

    if (index === numChildren - 1) {
      return child
    }

    let finalSpacing = defaultSpacing
    const nextChild = childArray[index + 1]

    if (nextChild !== null && isValidElement(nextChild)) {
      const nextName = elementTypeName(nextChild)

      finalSpacing =
        componentSpacing[currentName]?.[nextName] ??
        componentSpacing.default[nextName] ??
        componentSpacing[currentName]?.default ??
        defaultSpacing
    }

    if (!nextChild) {
      finalSpacing = 0
    }

    return <>{cloneElement(child, { [spacingProp]: finalSpacing })}</>
  })

  return (
    <Flex
      direction={direction}
      alignItems="flex-start"
      spacing={0}
      {...otherProps}
    >
      {mappedChildren}
    </Flex>
  )
}
